//import { createHashHistory } from 'history';
import * as router from 'react-router-dom';
import qhistory from 'qhistory';
import { stringify, parse } from 'qs';
import { createBrowserHistory, Location } from 'history';

export * from 'react-router-dom';

const ENTRIES_TO_SKIP_KEY = 'entriesToSkip';
const canLogHistoryOperations = false;

interface ILocation<Q> extends Location {
    query: Q;
}

export interface IQueryableRouteComponentProps<TParams, TQuery> extends router.RouteComponentProps<TParams> {
    location: ILocation<TQuery>;
}

let h = qhistory(createBrowserHistory(), stringify, parse); //qhistory(createHashHistory(), stringify, parse);
export const history = () => h;

const subscribersForPageChanged: (() => void)[] = [];

h.listen((/*_location: Location*/) => {
    subscribersForPageChanged.forEach(callback => { callback(); });
});

export const subscribeForPageChanged = (callback: () => void): void => {
    subscribersForPageChanged.push(callback);
}

let historyLengthWhenOpened = h.length;

function getEntriesToSkipFromStorage(): number {
    //can use real window.sessionStorage also for isInsideMobileApp
    let value = window.sessionStorage.getItem(ENTRIES_TO_SKIP_KEY);
    if (value) {
        return parseInt(value, 10);
    }
    return 0;
}

export function push(path: string, ignoreInHistory?: boolean) {
    //can use real window.sessionStorage also for isInsideMobileApp
    if (ignoreInHistory) {
        window.sessionStorage.setItem(ENTRIES_TO_SKIP_KEY, `${getEntriesToSkipFromStorage() + 1}`);
    } else {
        clearEntriesToSkip();
    }
    if (canLogHistoryOperations) {
        window.console.info("history.push", path);
    }
    h.push(path);
    //console.info("History historyLengthWhenOpened: " + historyLengthWhenOpened + ", h.length: " + h.length + ", etoskip: " + getEntriesToSkipFromStorage());
}

function clearEntriesToSkip() {
    if (canLogHistoryOperations) {
        window.console.info("clearEntriesToSkip");
    }
    window.sessionStorage.setItem(ENTRIES_TO_SKIP_KEY, '0');
}

export function pushWithQuery(path: string, query: any, ignore?: boolean) {
    //can use real window.sessionStorage also for isInsideMobileApp
    if (ignore) {
        window.sessionStorage.setItem(ENTRIES_TO_SKIP_KEY, `${getEntriesToSkipFromStorage() + 1}`);
    } else {
        window.sessionStorage.setItem(ENTRIES_TO_SKIP_KEY, '0');
    }
    const result = `${path}?${stringify(query)}`;
    if (canLogHistoryOperations) {
        window.console.info("history.push", result);
    }
    h.push(result);
}

export function redirect(url: string, external?: boolean, withIgnoreAndClear: boolean = false) {
    if (!url.startsWith('http')) {
        if (!url.startsWith('/')) {
            throw new Error("Unexpected - in redirect url is not starting with /");
        }
        if (withIgnoreAndClear) {
            clearEntriesToSkip(); // maybe very specific for Tipos, moj-ticketl
            if (canLogHistoryOperations) {
                window.console.info("history.push, with ignore history", url);
            }
            push(url, true);
        } else {
            if (canLogHistoryOperations) {
                window.console.info("history.push", url);
            }
            h.push(url);
        }
    } else if (external) {
        window.open(url);
    } else {
        window.location.href = url;
    }
}

/**
 * Usually home is at /. But live is at /live, thus parameter is added to remind.
 */
export function goHome(path: string = null) {
    if (canLogHistoryOperations) {
        window.console.info("history.push", path === null ? '/' : path);
    }
    h.push(path === null ? '/' : path);
}

export function goBack() {
    const entriesToSkip = getEntriesToSkipFromStorage();

    if (entriesToSkip > 0) {
        if (canLogHistoryOperations) {
            window.console.info("go back more entries", -(entriesToSkip + 1));
        }
        h.go(-(entriesToSkip + 1));
    } else {
        if (canLogHistoryOperations) {
            window.console.info("go back");
        }
        h.goBack();
    }
}

/**
 * for e.g. event, bet details which may be opened by external redirecting request from desktop web
 *
 * check e.g. on mobile: going back from detail event or ticket), the same after refresh of the page of detail and the same when it's opened on the new tab. And all of it after changing a tab at the top.
 * ! browser on mobile phone may have a different behavior than browser on desktop!
 *
 * ! this solution has a problem when user changes tabs, than clicks button back and then clicks the arrow back on UI - entriesToSkip does not changes on browser back or forward button click.
 *
 * !! see also at end calling clearEntriesToSkip at begin (was added later to avoid too often redirect to home)
 */
export function goBackOrHome(goToSpecificHome?: () => void) {
    //can use real window.sessionStorage also for isInsideMobileApp
    const entriesToSkip = getEntriesToSkipFromStorage();

    if (historyLengthWhenOpened > 0 && h.length - entriesToSkip > historyLengthWhenOpened) {  // the last page in history is current page, thus > (not >=) (open event detail in a new tab and click Back). But after refresh it goes to home as it does not know if previous pages are from this web (but is refresh so often? maybe it's possible to use a local storage)
        // historyLengthWhenOpened seems to be always > 0
        // first in history is the entry page, e.g. event detail from notification
        if (entriesToSkip > 0) {
            if (canLogHistoryOperations) {
                window.console.info("go back more entries", -(entriesToSkip + 1));
            }
            h.go(-(entriesToSkip + 1)); //when going forward, it brokes.
        } else {
            if (canLogHistoryOperations) {
                window.console.info("go back");
            }
            h.goBack();
        }
    } else {
        if (goToSpecificHome) {
            goToSpecificHome();
        } else {
            goHome();
        }
    }
}

export function hasPrevLink() {
    return h.length > 2;
}

const monitoringQueryParamsToAdd = ["_gl", "gclid"];

/**
 * Adds specific query params (monitoringQueryParamsToAdd) from url to new url, to preserve it to be read (e.g. by Google Analytics). Use with care in containers!
 */
export function addMonitoringQueryParams(baseUrl: string): string {
    const searchParams = new URLSearchParams(window.document.location.search);
    let newUrl = baseUrl; // can't use this as it's relative new URL(baseUrl)
    monitoringQueryParamsToAdd.forEach((v) => {
        if (searchParams.has(v)) {
            if (newUrl.indexOf("?") > 0) {
                newUrl += "&";
            } else {
                newUrl += "?";
            }
            newUrl += v + "=" + encodeURIComponent(searchParams.get(v));
        }
    });
    return newUrl;
}

export function getSearchParams(): URLSearchParams {
    const { search } = window.location;
    return new URLSearchParams(search);
}

/*
In ticket detail - there was an error, that entriesToSkip remained and back didn't worked. Who was responsible for reset? or why is it in session storage?
But reset EntriesToSkip on start breaks clicking on tab in mobile event detail.
reset EntriesToSkip on start, is it correct? why it's stored in session then?
But when I had EntriesToSkip = 1 and opens the bet detail from RPP, back didn't work, it went to SB home. After clear of EntriesToSkip it worked.

-> result: clear entries on all pages except on mobile event details as there are tabs.
On legacy ticket detail after refresh of the page it loose entrySkipPage and then "back" goes to ticket detail and to legacy detail. But combination of legacy ticket and refresh is not so often.
! It seems that e.g. Chrome and Edge count home page as the page one in history. And the number 2 is just opened page. In Chrome back on ticket detail opens empty initial page but on edge there is a "content" (news).
*/
if (!(window.location.pathname.startsWith("/zapas/") || window.location.pathname.startsWith("/live-zapas/"))) {  // TODO generalize names
    clearEntriesToSkip();
}
